// src/contexts/AuthContext.jsx
// Contexto global de autenticación con Supabase Auth
// Maneja estado de sesión, login, logout y escucha cambios de auth

import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

// Crear contexto
const AuthContext = createContext(null);

/**
 * Hook para acceder al contexto de autenticación
 * @returns {Object} { user, loading, error, signIn, signOut }
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de un AuthProvider');
  }
  return context;
};

/**
 * Provider de autenticación
 * Envuelve la app y provee estado de auth a todos los componentes
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ============================================
  // EFECTO: Inicializar sesión y escuchar cambios
  // ============================================
  useEffect(() => {
    // Obtener sesión actual al montar
    const initializeAuth = async () => {
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error('Error obteniendo sesión:', sessionError);
          setError(sessionError.message);
        } else {
          setUser(session?.user ?? null);
        }
      } catch (err) {
        console.error('Error inicializando auth:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();

    // Escuchar cambios de autenticación (login, logout, token refresh)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('🔐 Auth event:', event);
        setUser(session?.user ?? null);
        setError(null);
      }
    );

    // Cleanup: desuscribirse al desmontar
    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  // ============================================
  // FUNCIÓN: Iniciar sesión con email y password
  // ============================================
  const signIn = async (email, password) => {
    try {
      setError(null);
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (signInError) {
        throw signInError;
      }

      return { success: true, user: data.user };
    } catch (err) {
      console.error('Error en login:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // ============================================
  // FUNCIÓN: Cerrar sesión
  // ============================================
  const signOut = async () => {
    try {
      setError(null);
      const { error: signOutError } = await supabase.auth.signOut();
      
      if (signOutError) {
        throw signOutError;
      }

      return { success: true };
    } catch (err) {
      console.error('Error en logout:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // ============================================
  // FUNCIÓN: Enviar email de recuperación de contraseña
  // ============================================
  const resetPassword = async (email) => {
    try {
      setError(null);
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (resetError) {
        throw resetError;
      }

      return { success: true };
    } catch (err) {
      console.error('Error enviando reset:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // ============================================
  // VALOR DEL CONTEXTO
  // ============================================
  const value = {
    user,
    loading,
    error,
    signIn,
    signOut,
    resetPassword,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;